import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { DocumentNode, FragmentDefinitionNode, Kind, OperationDefinitionNode, OperationTypeNode } from "graphql";
import { CBDataPoints } from "../models/flexible/cb-data-points";
import { DiscontinuationDataPoints } from "../models/flexible/discontinuation-data-points";
import { DiscontinuationNoteDataPoints } from "../models/flexible/discontinuation-note-data-points";
import { fieldControllerFactory } from "../models/flexible/field-controller-factory";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexEntitySchema } from "../models/flexible/flex-entity-schema";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { FlexSectionSchema } from "../models/flexible/flex-section-schema";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { IRDataPoints } from "../models/flexible/ir-data-points";
import { RecordDataPoints } from "../models/flexible/record-data-points";
import { sectionControllerFactory } from "../models/flexible/section-controller-factory";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { combinePaths, isSectionEmbedded } from "../models/flexible/utils";
import { FlexEntityType, FlexFormStatus } from "../models/generated/graphql";
import { distinctBy } from "./distinct-by";

const firstNonEmpty = <T, R>(items: Array<T>, mapper: (item: T) => R) => {
	for (const item of items) {
		const result = mapper(item);
		if (!!result) {
			return result;
		}
	}
	return undefined;
};

const returnFirstResult = <T, R>(items: Array<T>, mapper: (e: T) => R): R | undefined => {
	for (const item of items) {
		const itemResult = mapper(item);
		if (!!itemResult) {
			return itemResult;
		}
	}
	return undefined;
};

export const getSectionDataWithAttribute = <T>(data: any, attributeId: string): T => {
	if (Array.isArray(data)) {
		const nonEmptyItems = data.map((e) => getSectionDataWithAttribute(e, attributeId)).filter(Boolean);
		return nonEmptyItems.length === 0 ? (undefined as any) : (nonEmptyItems as any);
	}

	if (!data || typeof data !== "object") {
		return undefined as any;
	}

	return attributeId in data ? data : firstNonEmpty(Object.keys(data), (key) => getSectionDataWithAttribute(data[key], attributeId));
};

export const getInitialResponse = (entities: Array<TypedFlexEntity<any>>) => {
	return returnFirstResult(
		entities.filter((entity) => entity.status !== FlexFormStatus.Draft),
		(c) => getSectionDataWithAttribute<IRDataPoints>(c.data, AttributeIdentifiers.TherapyStartDate)
	);
};

export const getDiscontinuationNote = (entities: Array<TypedFlexEntity<any>>) => {
	return returnFirstResult(entities, (container) =>
		getSectionDataWithAttribute<DiscontinuationNoteDataPoints>(container.data, AttributeIdentifiers.DiscontinuationDate)
	);
};

export const getContinuousBenefits = (entities: Array<TypedFlexEntity<any>>): Array<CBDataPoints> => {
	return entities
		.filter((entity) => entity.status !== FlexFormStatus.Draft)
		.map((container) => getSectionDataWithAttribute<CBDataPoints>(container.data, AttributeIdentifiers.BenefitAssessmentDate))
		.filter(Boolean);
};

export const getFormPrescriptions = (entities: Array<TypedFlexEntity<any>>) => {
	return entities
		.filter((entity) => entity.status !== FlexFormStatus.Draft)
		.flatMap((c) => getSectionDataWithAttribute<Array<RecordDataPoints>>(c.data, AttributeIdentifiers.PrescriptionDate))
		.filter(Boolean);
};

export const getDiscontinuationDataPoints = (entities: Array<TypedFlexEntity<any>>): Array<DiscontinuationDataPoints> => {
	return entities
		.filter((entity) => entity.status !== FlexFormStatus.Draft)
		.map((container) =>
			getSectionDataWithAttribute<DiscontinuationDataPoints>(container.data, AttributeIdentifiers.PatientEnrolledInTreatment)
		)
		.filter(Boolean);
};

export const getDiscontinuationNoteDataPoints = (entities: Array<TypedFlexEntity<any>>): Array<DiscontinuationNoteDataPoints> => {
	return entities
		.filter((entity) => entity.status !== FlexFormStatus.Draft)
		.map((container) =>
			getSectionDataWithAttribute<DiscontinuationNoteDataPoints>(container.data, AttributeIdentifiers.DiscontinuationDate)
		)
		.filter(Boolean);
};

export const combineQueries = (queries: Array<DocumentNode>): DocumentNode => {
	const fragmentDefinitions = queries.flatMap((q) =>
		q.definitions.filter((d) => d.kind === Kind.FRAGMENT_DEFINITION)
	) as Array<FragmentDefinitionNode>;
	const operationDefinitions = queries.flatMap((q) =>
		q.definitions.filter((d) => d.kind === Kind.OPERATION_DEFINITION)
	) as Array<OperationDefinitionNode>;
	const selections = operationDefinitions.flatMap((d) => d.selectionSet.selections);
	const variables = distinctBy(
		operationDefinitions.flatMap((d) => d.variableDefinitions || []),
		(variable) => variable.variable.name.value
	);
	const distinctFragments = distinctBy(fragmentDefinitions, (f) => f.name.value);

	return {
		kind: Kind.DOCUMENT,
		definitions: [
			...distinctFragments,
			{
				kind: Kind.OPERATION_DEFINITION,
				operation: OperationTypeNode.QUERY,
				name: {
					kind: Kind.NAME,
					value: "FlexEntityDependencies",
				},
				selectionSet: {
					kind: Kind.SELECTION_SET,
					selections,
				},
				variableDefinitions: variables,
			},
		],
	};
};

export const getInitialFormDataQuery = (config: InitialRequestConfig): DocumentNode => {
	const uniqueQueries = Array.from(new Set(config.queries));
	return combineQueries(uniqueQueries);
};

export const setFlexControllerReferences = (
	section: FlexSectionController,
	parent: FlexSectionController | undefined,
	type: FlexContext
) => {
	section.context = type;
	section.parent = parent;
	section.fieldControllers.forEach((f) => (f.parent = section));
	section.sectionControllers.forEach((s) => setFlexControllerReferences(s, section, type));
};

export const createSectionController = (
	schema: FlexEntitySchema,
	entityType: FlexEntityType | undefined,
	section: FlexSectionSchema,
	parentPath: string
): FlexSectionController => {
	const path = isSectionEmbedded(section) ? parentPath : combinePaths(parentPath, section.localizableKey);
	const fieldModels = section.fields.map((field) =>
		fieldControllerFactory(field, section, combinePaths(path, field.attribute.identifier))
	);
	const nestedSectionModels = (section.sections || []).map((nestedSection) =>
		createSectionController(schema, entityType, nestedSection, path)
	);
	return sectionControllerFactory(entityType, section, path, fieldModels, nestedSectionModels);
};
